import React, { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router";

import {
  COLLECTION_ID,
  FileEntity,
  Space,
  Track,
  Id,
} from "@highnote/server/src/core/entities";
import {
  getEntitySubscribers,
  getSpaceTrackRowId,
  isSpacePrivateInboxEnabled,
  sortItemsByItemsOrder,
} from "@highnote/server/src/core/shared-util";
import { highnote } from "@highnote/server/src/sdk";
import { DefaultAttachmentActions } from "App/common/AttachmentActions";
import {
  BaseRow,
  BaseTableRowProps,
  EXPAND_COLUMN_WIDTH,
  EXPAND_COLUMN_WIDTH_MOBILE,
  TableRowVariant,
  calculateChildIndentation,
  getEntityCollectionFromType,
} from "App/common/BaseTableV2";
import { Skeleton } from "App/common/Skeleton/Skeleton";
import {
  AttachmentsContextProvider,
  useAttachmentsContext,
} from "App/common/useAttachments";
import { Confirmator } from "App/common/useConfirmation";
import { useFileDownload } from "App/common/useFileDownload";
import { useViewport } from "App/common/useViewport";
import { getAuthId, useAuth } from "App/components/Auth";
import { useDnDMonitor } from "App/components/DragAndDrop/DnDMonitor";
import { DnDWrapper } from "App/components/DragAndDrop/DnDWrapper";
import { useGlobalAudioPlayer } from "App/components/GlobalAudioPlayer";
import { useCreatedTracks } from "App/components/useEntities/useCreatedTracks";
import { DefaultTrackActions } from "App/components/useEntities/useLibraryTracks/TrackActions";
import { useUser } from "App/components/useEntities/useUser";
import { useEntitiesSelection } from "App/components/useEntitiesSelection";
import {
  sortRowByAlphabetical,
  sortRowByCreatedAt,
  sortRowByCreatedAtAscending,
} from "App/components/util";
import { BUTTON_THEME } from "App/core/Button";
import { withMobileDeeplinkRoot } from "App/modules/urls";
import { isTouchDevice } from "App/modules/utils";
import { SORT_TYPE } from "App/routes/Main/Library/config";
import { useChildEntityWatchers } from "App/store/helpers/useChildEntityWatchers";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { useGlobalSpaceTracks } from "App/store/spaceTracks/useGlobalSpaceTracks";
import { useGlobalTracks } from "App/store/tracks/useGlobalTracks";
import {
  RenderableTrack,
  SpaceTrackTile,
  buildSpaceTrackTile,
} from "App/store/tracks/buildSpaceTrackTile";
import {
  ENTITY_TYPE,
  EntityRowConfig,
  ENTITY_PREVIEW_METADATA,
  PreviewMetadataField,
} from "../config";
import { SpaceStub } from "App/components/useEntities/spaceStub";
import { LazySpaceActions } from "App/components/useEntities/LazySpaceActions";
import { ExpandableParentTrackRow } from "../ExpandableTrack/ExpandableTrackRow";
import styles from "./ExpandableSpaceRow.module.scss";
import { asViewingSpaceId, ViewingSpaceId } from "App/modules/viewingSpace";

export const childItemRowsToBulkSelections = (
  rows: EntityRowConfig[],
  parentEntityId: string,
  parentEntityType: COLLECTION_ID.SPACE | COLLECTION_ID.TRACK,
) => {
  const bulkSelections = rows.reduce((acc, itemRow) => {
    const include =
      parentEntityType === COLLECTION_ID.SPACE
        ? itemRow.type === ENTITY_TYPE.SPACE ||
          itemRow.type === ENTITY_TYPE.TRACK ||
          itemRow.type === ENTITY_TYPE.TRACK_VERSION ||
          itemRow.type === ENTITY_TYPE.FILE
        : itemRow.type === ENTITY_TYPE.TRACK_VERSION ||
          itemRow.type === ENTITY_TYPE.FILE;
    if (include) {
      const entityType = getEntityCollectionFromType(itemRow.type);
      acc.push({
        entityId: (itemRow.entity as Entity).id,
        entityType,
        parentEntity: {
          entityId: parentEntityId,
          entityType: parentEntityType,
        },
        // The cascade only ever produces TRACK entityType children when
        // parentEntityType is SPACE (a track can't be selected because a
        // TRACK parent was selected) — parentEntityId is that containing
        // (viewing) space, never the track's own origin. See the identical
        // spaceId contract in SpaceItems.tsx's onSelectRow.
        ...(entityType === COLLECTION_ID.TRACK && { spaceId: parentEntityId }),
      });
    }
    return acc;
  }, []);
  return bulkSelections;
};

/**
 * 'useFormatSpaceToBaseEntityRow', 'useFormatTrackToBaseEntityRow', and 'useFormatFileToBaseEntityRow' create
 * a row configuration object for entities (Space, Track, File) in the EntityTable component.
 * These functions ensures consistency and ease of maintenance across different components.
 * Used via useSpaceChildItemRows in: ExpandableSpaceRow.tsx, EntityLibrary.tsx, SpaceItems.tsx.
 *
 * Table Rows:
 * - Track Row (Flat, expandable with versions)
 * - Attachment/File Row (Flat)
 * - Space Row (Expanded, Click-Thru, Flat)
 */

export type EntityTableRowOptions = {
  hasPlayableTracks?: boolean;
  tableRowVariant?: TableRowVariant;
  // Overrides which metadata pieces EntityPreview renders for the row's byline.
  // When omitted, EntityPreview falls back to its verbose per-type default.
  entityPreviewMetadata?: PreviewMetadataField[];
};

export const useFormatSpaceToBaseEntityRow = () => {
  const history = useHistory();

  const formatSpaceToBaseEntityRow = ({
    space,
    options,
  }: {
    /**
     * Under the new read model this is a `SpaceStub`: Library,
     * `SearchEntityList`, and `ClickThruSpaceRow` all feed it entries out of
     * `useTopLevelSpaces` / `useLibrarySpaces`, and a stub carries no role
     * data. `LazySpaceActions` below is the defence: it fetches the space
     * document before the real menu renders. See its header for the full
     * explanation.
     */
    space: SpaceStub;
    options?: EntityTableRowOptions;
  }): EntityRowConfig => {
    const tableRowVariant = options?.tableRowVariant;

    return {
      key: space.id,
      id: space.id,
      entity: space as Space,
      type: ENTITY_TYPE.SPACE,
      createdAt: space.createdAt,
      name: space.name,
      entityPreviewMetadata: options?.entityPreviewMetadata,
      actions: (
        <LazySpaceActions space={space} componentId="click-thru-space-row" />
      ),
      onClick: () => {
        history.push(`/space/${space.id}`);
      },
      attributes:
        tableRowVariant === "nested-click-thru"
          ? { "data-is-nested": "true" }
          : undefined,
      NestedTable:
        tableRowVariant === "expanded-row" ||
        tableRowVariant === "draggable-expanded-row"
          ? ExpandableParentSpaceRow
          : undefined,
    };
  };

  return { formatSpaceToBaseEntityRow };
};

export const trackDeeplinkConfirmPayload = {
  wrapperClassName: "track-open-in-mobile-confirm-dialog",
  body: "Open Track",
  continueCTAConfig: {
    label: "Open in App",
    theme: BUTTON_THEME.CTA,
  },
  cancelCTAConfig: {
    label: "Continue in Browser",
    theme: BUTTON_THEME.SECONDARY,
  },
};

export const useFormatTrackToBaseEntityRow = () => {
  const { user } = useUser();
  const { shareKey } = useAuth();
  const history = useHistory();
  const { isMobile: isMobileViewport } = useViewport();
  const isMobile = isMobileViewport || isTouchDevice();

  const formatTrackToBaseEntityRow = ({
    track,
    options,
    viewingSpaceId,
  }: {
    track: RenderableTrack;
    options?: EntityTableRowOptions;
    /** The space this row is listed in — see `EntityData.viewingSpaceId`. */
    viewingSpaceId?: ViewingSpaceId;
  }): EntityRowConfig => {
    // Carried on the ROW (this config), not just the entity — see
    // `EntityData.viewingSpaceId` (EntityTable/config.ts). The caller's
    // value (the space this list belongs to) wins; a row-backed
    // `SpaceTrackTile` (buildSpaceTrackTile) already carries the same
    // space; a plain `Track` (Search, Library, ...) has neither, so this is
    // simply undefined there.
    const rowViewingSpaceId = viewingSpaceId ?? track.viewingSpaceId;
    return {
      key: track.id,
      id: track.id,
      entity: track,
      type: ENTITY_TYPE.TRACK,
      viewingSpaceId: rowViewingSpaceId,
      actions: (
        <DefaultTrackActions track={track} viewingSpaceId={rowViewingSpaceId} />
      ),
      createdAt: track.createdAt,
      name: track.title,
      entityPreviewMetadata: options?.entityPreviewMetadata,
      // Navigation must stay in the space this tile is being VIEWED under,
      // never jump to the track's own origin (`track.spaceId`) — the
      // origin may be a space the viewer can't even read. Only a plain
      // `Track` with no viewing space (Search, Library, ...) falls back
      // to `track.spaceId`, which is the only spaceId it has.
      onClick: () => {
        history.push(
          `/space/${rowViewingSpaceId ?? track.spaceId}/${track.id}`,
        );
      },
      isPlayable: options?.hasPlayableTracks,
      ...(user?.isMobileUser &&
        isMobile && {
          confirmOnClick: (confirm: Confirmator) =>
            confirm(trackDeeplinkConfirmPayload),
          onConfirmCancel: () => {
            window.location.href = withMobileDeeplinkRoot(
              `root/library/${rowViewingSpaceId ?? track.spaceId}/${track.id}`,
              shareKey,
            );
          },
        }),
      NestedTable: (() => {
        if (options?.tableRowVariant !== "expanded-row") return undefined;
        const hasChildren =
          (track.versionFilesV2?.length || 0) + (track.files?.length || 0) > 1;
        return hasChildren ? ExpandableParentTrackRow : undefined;
      })(),
    };
  };

  const formatTracksListToBaseEntityRows = ({
    tracks,
    options,
    viewingSpaceId,
  }: {
    tracks: RenderableTrack[];
    options?: EntityTableRowOptions;
    viewingSpaceId?: ViewingSpaceId;
  }) => {
    return tracks.map((track) => {
      return formatTrackToBaseEntityRow({ track, options, viewingSpaceId });
    });
  };

  return { formatTrackToBaseEntityRow, formatTracksListToBaseEntityRows };
};

export const useFormatFileToBaseEntityRow = () => {
  // Assumes all components using this function are wrapped with the AttachmentsContextProvider
  const { canEdit } = useAttachmentsContext();
  const { downloadFile } = useFileDownload();

  const formatFileToBaseEntityRow = (
    file: FileEntity,
    options?: EntityTableRowOptions,
  ): EntityRowConfig => {
    return {
      key: file.id,
      id: file.id,
      entity: file,
      type: ENTITY_TYPE.FILE,
      actions: <DefaultAttachmentActions attachment={file} />,
      createdAt: file.createdAt,
      name: file.name,
      entityPreviewMetadata: options?.entityPreviewMetadata,
      onClick: file.url
        ? () => window.open(file.url, "_blank")
        : file.storagePath && canEdit
          ? () => downloadFile(file.id)
          : undefined,
    };
  };

  return { formatFileToBaseEntityRow };
};

export const getSortedChildRows = ({
  childItemRows,
  sortType,
  itemsOrder,
}: {
  childItemRows: EntityRowConfig[];
  sortType: SORT_TYPE;
  // Used to track order changes via drag and drop
  // TODO: Optimistic update benefit, but is this needed, or can we rely on backend to update the order via space.itemsOrder?
  itemsOrder?: string[];
}): EntityRowConfig[] => {
  if (sortType !== SORT_TYPE.CUSTOM) {
    return childItemRows.slice().sort((aRow, bRow) => {
      if (sortType === SORT_TYPE.ALPHABETICAL) {
        return sortRowByAlphabetical(aRow, bRow);
      }
      return sortRowByCreatedAt(aRow, bRow);
    });
  }

  const { orderedItems, unOrderedItems } = sortItemsByItemsOrder(
    itemsOrder || [],
    childItemRows as { id: Id; entity: Entity }[],
  );

  unOrderedItems.sort((aRow, bRow) => {
    return sortRowByCreatedAtAscending(
      aRow as EntityRowConfig,
      bRow as EntityRowConfig,
    );
  });

  return [...orderedItems, ...unOrderedItems];
};

export const useSpaceChildItemRows = ({
  space,
  options,
}: {
  space: Space;
  options?: EntityTableRowOptions;
}) => {
  const { globalSpaces, globalSpacesWatchers, getGlobalSpace } =
    useGlobalSpaces();
  const { globalTracks } = useGlobalTracks();
  const { globalSpaceTracks } = useGlobalSpaceTracks();
  const { createdTracks } = useCreatedTracks();
  const { attachments } = useAttachmentsContext();
  const { formatSpaceToBaseEntityRow } = useFormatSpaceToBaseEntityRow();
  const { formatTrackToBaseEntityRow } = useFormatTrackToBaseEntityRow();
  const { formatFileToBaseEntityRow } = useFormatFileToBaseEntityRow();

  const childSpacesLoading = globalSpacesWatchers
    .get(space.id)
    ?.loading.get("space-home-child-item-rows");

  const spaceId = space.id;
  const childSpaces = globalSpaces.childSpaces.get(spaceId) || [];
  const childTracks = globalTracks.childTracks.get(spaceId) || [];

  const childItemRows = useMemo(() => {
    const userId = getAuthId();
    const subscribers = getEntitySubscribers(space);

    const isInboxGuest =
      isSpacePrivateInboxEnabled(space) && !subscribers.includes(userId);

    const tracks = isInboxGuest
      ? createdTracks
      : // `childTracks` (the union of the legacy per-space query and
        // space-tracks-v1 rows — see `useGlobalTracks`) can hold ids for a
        // borrowed track whose canonical Track doc was never fetched by the
        // legacy query. `buildSpaceTrackTile` resolves those from their
        // space-tracks-v1 row instead — a row-backed tile needs no
        // canonical Track doc at all. Only an id backed by NEITHER a row
        // NOR a resolved Track (unreachable today given how childTracks
        // membership is populated, but defended anyway) still returns
        // `undefined` and is filtered here, rather than let it reach
        // `formatTrackToBaseEntityRow` below, which dereferences fields on
        // the track unconditionally.
        childTracks
          .map((childTrackId) =>
            buildSpaceTrackTile({
              row: globalSpaceTracks.spaceTrackRows.get(
                getSpaceTrackRowId(spaceId, childTrackId),
              ),
              track: globalTracks.tracks.get(childTrackId),
              // Minted, not route-derived: this is the space whose
              // `space-tracks-v1` rows produced this tile (same id as the
              // row lookup above), so by construction it IS the space the
              // tile is displayed under. For a nested space that is more
              // precise than `useUrlContext`, which resolves only the
              // route's top-level space.
              viewingSpaceId: asViewingSpaceId(spaceId),
            }),
          )
          .filter((tile): tile is SpaceTrackTile => Boolean(tile));

    const spaceTracks =
      (tracks || []).map((track) => {
        return formatTrackToBaseEntityRow({
          track,
          // These rows are listed IN this space — on its own page, or as an
          // expanded row on a parent's page. A list's containing space is
          // the second legitimate source of a viewing space (see
          // App/modules/viewingSpace.ts).
          viewingSpaceId: asViewingSpaceId(space.id),
          options: {
            hasPlayableTracks: options?.hasPlayableTracks,
            tableRowVariant: options.tableRowVariant,
            // Byline: [creator avatar] name • duration
            entityPreviewMetadata: [
              ENTITY_PREVIEW_METADATA.CREATED_BY_AVATAR,
              ENTITY_PREVIEW_METADATA.CREATOR_NAME,
              ENTITY_PREVIEW_METADATA.ITEM_COUNT,
            ],
          },
        });
      }) || [];

    const spaceChildSpaces =
      childSpaces.reduce<EntityRowConfig[]>((acc, childSpaceId) => {
        const childSpace = getGlobalSpace(childSpaceId, true);

        if (childSpace && !childSpace.isArchived) {
          acc.push(
            formatSpaceToBaseEntityRow({
              space: childSpace,
              options: {
                tableRowVariant: options?.tableRowVariant,
                // Byline: [creator avatar] name • N Items
                entityPreviewMetadata: [
                  ENTITY_PREVIEW_METADATA.CREATED_BY_AVATAR,
                  ENTITY_PREVIEW_METADATA.CREATOR_NAME,
                  ENTITY_PREVIEW_METADATA.DESCRIPTION,
                ],
              },
            }),
          );
        }
        return acc;
      }, []) || [];

    const spaceAttachments = isInboxGuest
      ? []
      : attachments?.map((file) =>
          formatFileToBaseEntityRow(file, {
            // Byline: [creator avatar] name • domain (bookmarks) / N MB (files).
            // The FILE branch drops whichever piece is null for the item.
            entityPreviewMetadata: [
              ENTITY_PREVIEW_METADATA.CREATED_BY_AVATAR,
              ENTITY_PREVIEW_METADATA.CREATOR_NAME,
              ENTITY_PREVIEW_METADATA.DOMAIN,
              ENTITY_PREVIEW_METADATA.FILE_SIZE,
            ],
          }),
        ) || [];

    return [...spaceTracks, ...spaceChildSpaces, ...spaceAttachments];
    // `globalSpaceTracks.spaceTrackRows` is included so a row-backed tile's
    // header (title, artwork, ...) updates when its row is rewritten, even
    // when `childTracks` membership itself hasn't changed. NOTE: it gets a
    // new Map identity on every space's SET_SPACE_TRACK_ROWS dispatch, not
    // just this one, so this recomputes somewhat more than strictly
    // necessary — same tradeoff `childTracks` itself had before it grew its
    // own per-space union cache (see useGlobalTracks.tsx).
  }, [
    childSpaces,
    childTracks,
    createdTracks,
    attachments,
    globalSpaceTracks.spaceTrackRows,
  ]);

  // Fetches all child spaces and tracks of the space
  const { manageGlobalChildEntityWatchers } = useChildEntityWatchers();

  useEffect(() => {
    if (!spaceId) return;

    const watcher = manageGlobalChildEntityWatchers({
      spaceId,
      // TODO: Consider using page/componentId as a unique identifier for the child item rows
      // as we can have multiple space-child-item-rows on a page poitning to the same spaceId
      // ie. SpacePicker and SpaceItems are both using space-child-item-rows but are different components
      // and pointing to the same spaceId.
      componentId: `space-home-child-item-rows`,
    });

    // Used to fetch child entities and set realtime watchers/listeners
    // for the current parent spaceId.
    watcher.attach();

    return () => {
      // Used to unsubscribe from realtime watchers/listeners
      // for the current parent spaceId.
      watcher.detach();
    };
  }, [spaceId]);

  const _getSortedChildRows = useCallback(
    ({
      sortType = SORT_TYPE.CUSTOM,
      itemsOrder,
    }: {
      sortType: SORT_TYPE;
      // Used to track order changes via drag and drop
      // TODO: Optimistic update benefit, but is this needed, or can we rely on backend to update the order via space.itemsOrder?
      itemsOrder?: string[];
    }): EntityRowConfig[] => {
      return useMemo(() => {
        return getSortedChildRows({
          sortType,
          childItemRows,
          itemsOrder: itemsOrder || space.itemsOrder,
        });
      }, [childItemRows, sortType, itemsOrder, space.itemsOrder]);
    },
    [childItemRows, space.itemsOrder],
  );

  return {
    getSortedSpaceChildItemRows: _getSortedChildRows,
    spaceChildRowsLoading: childSpacesLoading,
  };
};

const _ExpandableParentSpaceRow = ({
  space,
  parentRow,
  tableRowProps,
  sortType = SORT_TYPE.CUSTOM,
}: {
  space: Space;
  parentRow: EntityRowConfig;
  tableRowProps: Omit<BaseTableRowProps, "row"> & {
    hasPlayableTracks?: boolean;
  };
  sortType?: SORT_TYPE;
}) => {
  const { isMobile } = useViewport();
  const { fetchAudioFiles } = useGlobalAudioPlayer();
  const { globalSpaces } = useGlobalSpaces();
  const { registerDnDContainer, unregisterDnDContainer } = useDnDMonitor();
  const { getSortedSpaceChildItemRows, spaceChildRowsLoading } =
    useSpaceChildItemRows({
      space,
      options: {
        tableRowVariant: tableRowProps.tableRowVariant,
        hasPlayableTracks: tableRowProps.hasPlayableTracks ?? true,
      },
    });
  const { bulkAddToSelection, selectedEntities } = useEntitiesSelection();

  // TODO: `itemsOrder` will need to be passed here as well for optimistically ordering nested rows via the client (client side sorting).
  // Currently, it defaults to the space.itemsOrder (from the server) if not passed in.
  const spaceChildItemRows = getSortedSpaceChildItemRows({
    sortType,
  });

  const isSpaceSelected = space.id in selectedEntities;
  const allChildrenSelected = useMemo(() => {
    return (
      spaceChildItemRows.length > 0 &&
      spaceChildItemRows.every((itemRow) => itemRow.id in selectedEntities)
    );
  }, [spaceChildItemRows, selectedEntities]);

  useEffect(() => {
    // if space is selected, make sure all space children are selected
    if (isSpaceSelected) {
      const bulkSelections = childItemRowsToBulkSelections(
        spaceChildItemRows,
        space.id,
        COLLECTION_ID.SPACE,
      );
      bulkAddToSelection(bulkSelections);
      return;
    }
  }, [isSpaceSelected, allChildrenSelected, spaceChildItemRows]);

  useEffect(() => {
    spaceChildItemRows.forEach((itemRow) => {
      if (itemRow.type === ENTITY_TYPE.TRACK) {
        fetchAudioFiles(itemRow.entity as Track);
      }
    });
  }, [spaceChildItemRows]);

  useEffect(() => {
    registerDnDContainer(space.id, {
      getItemsOrder: () => spaceChildItemRows.map((row) => row.id),
      applyReorder: (newOrder: string[]) => {
        // Persist to backend; UI will refresh via existing watchers
        highnote.updateSpace({ id: space.id, data: { itemsOrder: newOrder } });
      },
      containerEntityType: ENTITY_TYPE.SPACE,
    });
    return () => {
      unregisterDnDContainer(space.id);
    };
  }, [spaceChildItemRows]);

  if (
    spaceChildRowsLoading &&
    spaceChildItemRows.length === 0 &&
    !globalSpaces.childSpaces.get(space.id)
  ) {
    const childIndentation = calculateChildIndentation(
      tableRowProps.indentation,
      isMobile,
    );

    const defaultColumnWidth = isMobile
      ? EXPAND_COLUMN_WIDTH_MOBILE
      : EXPAND_COLUMN_WIDTH;

    return (
      <div
        className={styles["skeleton-container"]}
        style={{
          paddingLeft: `${childIndentation + defaultColumnWidth}px`,
        }}
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={`skeleton-${index}`} height={40} />
        ))}
      </div>
    );
  }

  return (
    <>
      {spaceChildItemRows.map((childRow, i) => {
        const baseProps = {
          ...tableRowProps,
          parentRow,
          indentation: tableRowProps.indentation + 1,
        };

        // Single wrapper per child: provides both containerId and row id
        return (
          <DnDWrapper
            key={`expandable-space-row-${childRow.key}-${i}`}
            isDraggable
            isDropTarget
            dragData={{
              id: childRow.id,
              name: childRow.name,
              type: childRow.type,
              parentId: space.id,
              containerId: space.id,
            }}
            entityData={childRow}
            selectedEntities={selectedEntities}
            dropData={{
              id: childRow.id,
              containerId: space.id,
              type: childRow.type,
            }}
          >
            <BaseRow
              {...baseProps}
              enableDrag={sortType === SORT_TYPE.CUSTOM}
              row={{
                ...childRow,
                ...(childRow.type === ENTITY_TYPE.SPACE && {
                  NestedTable: ExpandableParentSpaceRow,
                }),
                ...(childRow.type === ENTITY_TYPE.TRACK &&
                  // Calculate total number of files/attachments and version files
                  ((childRow.entity as Track)?.files?.length || 0) +
                    ((childRow.entity as Track)?.versionFilesV2?.length || 0) >
                    1 && {
                    NestedTable: ExpandableParentTrackRow,
                  }),
                selected: childRow.id in selectedEntities,
              }}
            />
          </DnDWrapper>
        );
      })}
    </>
  );
};

const ExpandableParentSpaceRow = ({
  parentRow,
  tableRowProps,
  sortType,
}: {
  parentRow: EntityRowConfig;
  tableRowProps: Omit<BaseTableRowProps, "row">;
  sortType?: SORT_TYPE;
}) => {
  const parentSpace = parentRow.entity as Space;

  return (
    <AttachmentsContextProvider
      space={parentSpace}
      attachmentIds={parentSpace.files || []}
    >
      <_ExpandableParentSpaceRow
        space={parentSpace}
        parentRow={parentRow}
        tableRowProps={tableRowProps}
        sortType={sortType}
      />
    </AttachmentsContextProvider>
  );
};
